import React, { useEffect, useState, useCallback } from "react";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { Field, Modal } from "@/pages/Merchants";

export default function CardTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", form_factor: "virtual", currency: "USD", daily_limit: 5000, monthly_limit: 50000, description: "" });

  const load = useCallback(() => api.get("/admin/card-types").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/card-types", { ...form, daily_limit: Number(form.daily_limit), monthly_limit: Number(form.monthly_limit) });
      toast.success("Card type created");
      setOpen(false); setForm({ name: "", form_factor: "virtual", currency: "USD", daily_limit: 5000, monthly_limit: 50000, description: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="card-types-page">
        <PageHeader
          title="Card Types"
          subtitle="Define the card programmes merchants can issue against."
          actions={
            <button onClick={() => setOpen(true)} data-testid="create-card-type-btn" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150">
              <Plus size={14} weight="bold" /> New card type
            </button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <Surface key={c.id} className="p-5" testId={`card-type-${c.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{c.form_factor}</div>
                  <div className="font-display text-xl tracking-tight font-medium mt-0.5">{c.name}</div>
                </div>
                <StatusPill value={c.active ? "active" : "suspended"} />
              </div>
              <div className="space-y-2 text-sm">
                <KV k="Currency" v={c.currency} />
                <KV k="Daily limit" v={`$${c.daily_limit.toLocaleString()}`} />
                <KV k="Monthly limit" v={`$${c.monthly_limit.toLocaleString()}`} />
              </div>
              {c.description && <p className="text-xs text-zinc-500 mt-4">{c.description}</p>}
            </Surface>
          ))}
          {items.length === 0 && (
            <Surface className="col-span-full p-10 text-center text-sm text-zinc-500">No card types defined yet.</Surface>
          )}
        </div>

        {open && (
          <Modal title="New card type" onClose={() => setOpen(false)}>
            <form onSubmit={submit} className="space-y-3">
              <Field label="Name" testId="ct-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Form factor</label>
                  <select data-testid="ct-form-factor" value={form.form_factor} onChange={(e) => setForm({ ...form, form_factor: e.target.value })} className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none">
                    <option value="virtual">Virtual</option>
                    <option value="physical">Physical</option>
                  </select>
                </div>
                <Field label="Currency" testId="ct-currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Daily limit" testId="ct-daily" value={form.daily_limit} onChange={(v) => setForm({ ...form, daily_limit: v })} />
                <Field label="Monthly limit" testId="ct-monthly" value={form.monthly_limit} onChange={(v) => setForm({ ...form, monthly_limit: v })} />
              </div>
              <Field label="Description" testId="ct-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
                <button data-testid="submit-ct" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">Create</button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-500">{k}</span>
      <span className="font-mono tabular-nums">{v}</span>
    </div>
  );
}
